import { Transaction } from '../types/transaction';
import { format, startOfMonth, endOfMonth, subMonths } from 'date-fns';

export interface MonthlyPLData {
  month: string; // "2025-09" format
  incomeByCategory: Record<string, number>;
  expensesByCategory: Record<string, number>;
  totalIncome: number;
  totalExpenses: number;
  netIncome: number;
}

export interface PropertyPLReport {
  propertyId: string;
  propertyAddress: string;
  months: MonthlyPLData[];
  sixMonthAverage: {
    totalIncome: number;
    totalExpenses: number;
    netIncome: number;
  };
}

/**
 * Generate property P&L report from transactions
 */
export function generatePropertyPLReport(
  transactions: Transaction[],
  propertyId: string,
  propertyAddress: string,
  months: number = 6
): PropertyPLReport {
  // Filter to operational expenses only for this property
  const filtered = transactions.filter(t =>
    t.propertyId === propertyId &&
    t.expenseType === 'Operating'
  );

  // Get last N complete months (excluding current incomplete month)
  // If today is October 9, the last complete month is September
  const today = new Date();
  const lastCompleteMonth = subMonths(startOfMonth(today), 1); // Go back to start of last month
  const endDate = endOfMonth(lastCompleteMonth); // End of last complete month

  // Month keys in the window, oldest first
  const monthKeys: string[] = [];
  for (let i = months - 1; i >= 0; i--) {
    monthKeys.push(format(startOfMonth(subMonths(endDate, i)), 'yyyy-MM'));
  }
  const monthsInWindow = new Set(monthKeys);

  // Group by month using override_date if present, else date
  const byMonth = new Map<string, Transaction[]>();

  filtered.forEach(transaction => {
    const reportDate = transaction.overrideDate || transaction.date;
    // Compare month keys as strings. Parsing to a Date reads the value as UTC
    // midnight, which pushes 1st-of-month entries into the previous month in
    // timezones behind UTC and drops them from the window.
    const monthKey = reportDate.substring(0, 7); // Extract 'yyyy-MM' from 'yyyy-MM-dd'

    if (monthsInWindow.has(monthKey)) {
      if (!byMonth.has(monthKey)) {
        byMonth.set(monthKey, []);
      }
      byMonth.get(monthKey)!.push(transaction);
    }
  });

  // Generate monthly data
  const monthlyData: MonthlyPLData[] = [];

  monthKeys.forEach(monthKey => {
    const monthTransactions = byMonth.get(monthKey) || [];

    const incomeByCategory: Record<string, number> = {};
    const expensesByCategory: Record<string, number> = {};
    let totalIncome = 0;
    let totalExpenses = 0;

    monthTransactions.forEach(t => {
      if (t.amount > 0) {
        // Income
        incomeByCategory[t.category] = (incomeByCategory[t.category] || 0) + t.amount;
        totalIncome += t.amount;
      } else {
        // Expense (amount is negative)
        expensesByCategory[t.category] = (expensesByCategory[t.category] || 0) + Math.abs(t.amount);
        totalExpenses += Math.abs(t.amount);
      }
    });

    monthlyData.push({
      month: monthKey,
      incomeByCategory,
      expensesByCategory,
      totalIncome,
      totalExpenses,
      netIncome: totalIncome - totalExpenses
    });
  });

  // Calculate 6-month average
  const avgIncome = monthlyData.reduce((sum, m) => sum + m.totalIncome, 0) / monthlyData.length;
  const avgExpenses = monthlyData.reduce((sum, m) => sum + m.totalExpenses, 0) / monthlyData.length;

  return {
    propertyId,
    propertyAddress,
    months: monthlyData,
    sixMonthAverage: {
      totalIncome: avgIncome,
      totalExpenses: avgExpenses,
      netIncome: avgIncome - avgExpenses
    }
  };
}

export interface MonthlyOccupancy {
  month: string; // "2025-09" format
  occupied: number;
  total: number;
}

/**
 * Estimate how many units were occupied each month based on rent transactions.
 * Rent tagged with a unit counts that unit; untagged rent counts as one occupied
 * unit, since single-unit properties rarely tag their payments.
 */
export function calculateMonthlyOccupancy(
  transactions: Transaction[],
  totalUnits: number,
  monthKeys: string[]
): MonthlyOccupancy[] {
  const rentTransactions = transactions.filter(t => t.category === 'Rent' && t.amount > 0);

  return monthKeys.map(month => {
    const monthRent = rentTransactions.filter(
      t => (t.overrideDate || t.date).substring(0, 7) === month
    );

    const taggedUnits = new Set<string>();
    let hasUntagged = false;

    monthRent.forEach(t => {
      const unit = t.unit?.trim().toUpperCase();
      if (unit) {
        taggedUnits.add(unit);
      } else {
        hasUntagged = true;
      }
    });

    const occupied = taggedUnits.size + (hasUntagged ? 1 : 0);

    return {
      month,
      occupied: Math.min(occupied, totalUnits),
      total: totalUnits
    };
  });
}

/**
 * Get all unique income categories from report
 */
export function getIncomeCategories(report: { months: MonthlyPLData[] }): string[] {
  const categories = new Set<string>();
  report.months.forEach(m => {
    Object.keys(m.incomeByCategory).forEach(cat => categories.add(cat));
  });
  return Array.from(categories).sort();
}

/**
 * Get all unique expense categories from report
 */
export function getExpenseCategories(report: { months: MonthlyPLData[] }): string[] {
  const categories = new Set<string>();
  report.months.forEach(m => {
    Object.keys(m.expensesByCategory).forEach(cat => categories.add(cat));
  });
  return Array.from(categories).sort();
}

export interface PropertyBreakdown {
  propertyId: string;
  propertyAddress: string;
  totalIncome: number;
  totalExpenses: number;
  netIncome: number;
  lastMonthIncome: number;
  lastMonthExpenses: number;
  lastMonthNetIncome: number;
}

export interface PortfolioPLReport {
  months: MonthlyPLData[];
  sixMonthAverage: {
    totalIncome: number;
    totalExpenses: number;
    netIncome: number;
  };
  lastFullMonth: {
    totalIncome: number;
    totalExpenses: number;
    netIncome: number;
  };
  propertyBreakdowns: PropertyBreakdown[];
}

// Statuses excluded from the portfolio report. Compared case-insensitively
// because the data contains both 'Soft offer' and 'Soft Offer'.
const EXCLUDED_PORTFOLIO_STATUSES = ['soft offer', 'hard offer', 'opportunity'];

// Properties you actually hold. These always get a breakdown row, even with no
// activity in the window, so they stay reachable for editing.
const PORTFOLIO_STATUSES = ['operational', 'selling', 'needs tenant', 'rehab'];

function isActiveStatus(status?: string): boolean {
  return !EXCLUDED_PORTFOLIO_STATUSES.includes((status || '').toLowerCase());
}

function isPortfolioStatus(status?: string): boolean {
  return PORTFOLIO_STATUSES.includes((status || '').toLowerCase());
}

/**
 * Sum monthly occupancy across the given properties. The caller decides which
 * properties belong in the report; properties with no units are skipped.
 */
export function calculatePortfolioMonthlyOccupancy(
  properties: Array<{ id: string; units?: number | null; propertyUnits?: Array<unknown> }>,
  transactions: Transaction[],
  monthKeys: string[]
): MonthlyOccupancy[] {
  const totals = monthKeys.map(month => ({ month, occupied: 0, total: 0 }));

  properties.forEach(property => {
    const totalUnits = property.propertyUnits?.length || property.units || 0;
    if (totalUnits === 0) return;

    const propertyOccupancy = calculateMonthlyOccupancy(
      transactions.filter(t => t.propertyId === property.id && t.expenseType === 'Operating'),
      totalUnits,
      monthKeys
    );

    propertyOccupancy.forEach((o, i) => {
      totals[i].occupied += o.occupied;
      totals[i].total += o.total;
    });
  });

  return totals;
}

/**
 * Generate portfolio P&L report from all transactions
 * Excludes archived properties and properties in Soft Offer, Hard Offer, or Opportunity status
 */
export function generatePortfolioPLReport(
  transactions: Transaction[],
  properties: Array<{ id: string; address: string; archived?: boolean; status?: string }>,
  months: number = 6
): PortfolioPLReport {
  // Filter to active properties only (exclude archived and specific statuses)
  const activePropertyIds = new Set(
    properties.filter(p => !p.archived && isActiveStatus(p.status)).map(p => p.id)
  );

  // Filter to operational expenses only for active properties
  const filtered = transactions.filter(t =>
    t.expenseType === 'Operating' &&
    (t.propertyId === null || t.propertyId === 'business' || t.propertyId === undefined || activePropertyIds.has(t.propertyId))
  );

  // Get last N complete months (excluding the current incomplete month)
  const endDate = endOfMonth(subMonths(startOfMonth(new Date()), 1));

  // Month keys in the window, oldest first
  const monthKeys: string[] = [];
  for (let i = months - 1; i >= 0; i--) {
    monthKeys.push(format(startOfMonth(subMonths(endDate, i)), 'yyyy-MM'));
  }
  const monthsInWindow = new Set(monthKeys);

  // Group by month
  const byMonth = new Map<string, Transaction[]>();

  filtered.forEach(transaction => {
    const reportDate = transaction.overrideDate || transaction.date;
    // Compare month keys as strings. Parsing to a Date reads the value as UTC
    // midnight, which pushes 1st-of-month entries into the previous month in
    // timezones behind UTC and drops them from the window.
    const monthKey = reportDate.substring(0, 7); // Extract 'yyyy-MM' from 'yyyy-MM-dd'

    if (monthsInWindow.has(monthKey)) {
      if (!byMonth.has(monthKey)) {
        byMonth.set(monthKey, []);
      }
      byMonth.get(monthKey)!.push(transaction);
    }
  });

  // Generate monthly data (aggregate across all properties)
  const monthlyData: MonthlyPLData[] = [];

  monthKeys.forEach(monthKey => {
    const monthTransactions = byMonth.get(monthKey) || [];

    const incomeByCategory: Record<string, number> = {};
    const expensesByCategory: Record<string, number> = {};
    let totalIncome = 0;
    let totalExpenses = 0;

    monthTransactions.forEach(t => {
      if (t.amount > 0) {
        incomeByCategory[t.category] = (incomeByCategory[t.category] || 0) + t.amount;
        totalIncome += t.amount;
      } else {
        expensesByCategory[t.category] = (expensesByCategory[t.category] || 0) + Math.abs(t.amount);
        totalExpenses += Math.abs(t.amount);
      }
    });

    monthlyData.push({
      month: monthKey,
      incomeByCategory,
      expensesByCategory,
      totalIncome,
      totalExpenses,
      netIncome: totalIncome - totalExpenses
    });
  });

  // Calculate 6-month average
  const avgIncome = monthlyData.reduce((sum, m) => sum + m.totalIncome, 0) / monthlyData.length;
  const avgExpenses = monthlyData.reduce((sum, m) => sum + m.totalExpenses, 0) / monthlyData.length;

  // Last full month is the newest month in the window
  const lastFullMonthData = monthlyData[monthlyData.length - 1];
  const lastFullMonthKey = lastFullMonthData.month;

  // Property-level breakdown (totals across the window + last full month)
  const propertyMap = new Map(
    properties
      .filter(p => !p.archived && isActiveStatus(p.status))
      .map(p => [p.id, p.address])
  );
  const breakdowns: PropertyBreakdown[] = [];

  // Group by property, restricted to the reporting window so the period
  // totals match the months shown in the table. Held properties are seeded so
  // one that has gone quiet still gets a row.
  const byProperty = new Map<string, Transaction[]>();
  properties
    .filter(p => !p.archived && isPortfolioStatus(p.status))
    .forEach(p => byProperty.set(p.id, []));

  filtered.forEach(t => {
    const monthKey = (t.overrideDate || t.date).substring(0, 7);
    if (!monthsInWindow.has(monthKey)) return;

    const propId = t.propertyId || 'business';
    if (!byProperty.has(propId)) {
      byProperty.set(propId, []);
    }
    byProperty.get(propId)!.push(t);
  });

  byProperty.forEach((propTransactions, propId) => {
    let totalIncome = 0;
    let totalExpenses = 0;
    let lastMonthIncome = 0;
    let lastMonthExpenses = 0;

    propTransactions.forEach(t => {
      const reportDate = t.overrideDate || t.date;
      // Use date string directly to avoid timezone issues
      const transMonthKey = reportDate.substring(0, 7);
      const isLastMonth = transMonthKey === lastFullMonthKey;

      if (t.amount > 0) {
        totalIncome += t.amount;
        if (isLastMonth) lastMonthIncome += t.amount;
      } else {
        totalExpenses += Math.abs(t.amount);
        if (isLastMonth) lastMonthExpenses += Math.abs(t.amount);
      }
    });

    breakdowns.push({
      propertyId: propId,
      propertyAddress: propId === 'business' ? 'Business (No Property)' : propertyMap.get(propId) || propId,
      totalIncome,
      totalExpenses,
      netIncome: totalIncome - totalExpenses,
      lastMonthIncome,
      lastMonthExpenses,
      lastMonthNetIncome: lastMonthIncome - lastMonthExpenses
    });
  });

  // Worst last-month performers first, with the business row pinned to the end
  breakdowns.sort((a, b) => {
    if (a.propertyId === 'business') return 1;
    if (b.propertyId === 'business') return -1;
    return a.lastMonthNetIncome - b.lastMonthNetIncome;
  });

  return {
    months: monthlyData,
    sixMonthAverage: {
      totalIncome: avgIncome,
      totalExpenses: avgExpenses,
      netIncome: avgIncome - avgExpenses
    },
    lastFullMonth: {
      totalIncome: lastFullMonthData.totalIncome,
      totalExpenses: lastFullMonthData.totalExpenses,
      netIncome: lastFullMonthData.netIncome
    },
    propertyBreakdowns: breakdowns
  };
}
